import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, startOfWeek, endOfWeek, eachDayOfInterval, addDays, subDays, addWeeks, subWeeks, addMonths, subMonths, startOfMonth, endOfMonth } from 'date-fns';

import { useAuth } from '../context/AuthContext';
import { useTenant } from '../context/TenantContext';
import { meetingService, Meeting } from '../services/meetingService';
import { userService, User } from '../services/userService';
import { formatTime, parseDateAsUTC } from '../utils/dateUtils';
import { patientHmId, patientLabel } from '../utils/patientLabel';
import { useUserTimezone } from '../hooks/useUserTimezone';
import { getMeetingsForDayWithLayout, getHourMinuteInTimezone, PositionedMeeting } from '../utils/calendarLayout';
import { parseWallClock } from '../utils/dateUtils';
import { openPatientScheduling } from '../services/schedulingLinkService';
import AvailabilityLayer, { WorkingBlock } from '../components/calendar/AvailabilityLayer';
import AvailabilityEditor, { AvailabilitySave } from '../components/calendar/AvailabilityEditor';
import { availabilityService, AvailabilityResponse, AvailabilityBlock, availableHHMM } from '../services/availabilityService';
import { ContextMenu } from '../components/ui/ContextMenu';
import { EditCommentsModal } from '../components/modals/EditCommentsModal';
import { CancelMeetingModal } from '../components/modals/CancelMeetingModal';
import { NoShowModal } from '../components/modals/NoShowModal';
import { FinishTreatmentModal } from '../components/modals/FinishTreatmentModal';
import EmailSendDialog from '../components/dialogs/EmailSendDialog';
import SmsSendDialog from '../components/dialogs/SmsSendDialog';
import toast from 'react-hot-toast';

type ViewMode = 'day' | 'week' | '3days' | 'month';

const CalendarPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { currentTenant, refreshKey } = useTenant();
  const userTimezone = useUserTimezone();

  // Users without all_patients only see their own meetings in the calendar.
  const hasAllPatients = !!user?.user_privilege?.all_patients;
  const calendarTherapistId = hasAllPatients ? undefined : user?.id;

  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [meetings, setMeetings] = useState<Meeting[]>([]);

  // Therapist / "Only Mine" filters (only shown to all_patients users; they
  // filter the meetings already loaded from the server).
  const [therapists, setTherapists] = useState<User[]>([]);
  const [therapistFilter, setTherapistFilter] = useState<number | ''>('');
  const [onlyMine, setOnlyMine] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState<ViewMode>('week');

  // Context menu state
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; meeting: Meeting } | null>(null);

  // Modal states
  const [isEditCommentsModalOpen, setIsEditCommentsModalOpen] = useState(false);
  const [selectedMeetingForComments, setSelectedMeetingForComments] = useState<Meeting | null>(null);
  const [isSavingComments, setIsSavingComments] = useState(false);

  const [isCancelMeetingModalOpen, setIsCancelMeetingModalOpen] = useState(false);
  const [selectedMeetingForCancel, setSelectedMeetingForCancel] = useState<Meeting | null>(null);

  const [isNoShowModalOpen, setIsNoShowModalOpen] = useState(false);
  const [selectedMeetingForNoShow, setSelectedMeetingForNoShow] = useState<Meeting | null>(null);

  const [isFinishTreatmentModalOpen, setIsFinishTreatmentModalOpen] = useState(false);
  const [selectedMeetingForFinish, setSelectedMeetingForFinish] = useState<Meeting | null>(null);

  // Working hours for whoever the calendar is focused on. Fetched for the
  // visible range only, and re-fetched when that range or the therapist
  // changes - the alternative is holding a year of pattern in memory to
  // answer a question about one week.
  const [availability, setAvailability] = useState<AvailabilityResponse | null>(null);
  const [availabilityEditor, setAvailabilityEditor] = useState<{
    date: Date; start: string; end: string;
    mode: 'create' | 'edit' | 'blocked' | 'closed';
    source?: 'weekly' | 'date' | 'cancelled' | 'blocked';
    validFrom?: string | null; validTo?: string | null;
    comment?: string | null; availabilityId?: number | null;
  } | null>(null);
  const [savingAvailability, setSavingAvailability] = useState(false);
  const [availabilityError, setAvailabilityError] = useState<string | null>(null);

  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [smsDialogOpen, setSmsDialogOpen] = useState(false);
  const [selectedMeetingForContact, setSelectedMeetingForContact] = useState<Meeting | null>(null);

  // Calculate date range for API query based on view mode and current date
  const getDateRangeForView = useCallback((): { startDate: Date; endDate: Date } => {
    switch (viewMode) {
      case 'day':
        return { startDate: currentDate, endDate: currentDate };
      case '3days':
        return {
          startDate: currentDate,
          endDate: addDays(currentDate, 2)
        };
      case 'week':
        return {
          startDate: startOfWeek(currentDate, { weekStartsOn: 1 }),
          endDate: endOfWeek(currentDate, { weekStartsOn: 1 })
        };
      case 'month':
        const monthStart = startOfMonth(currentDate);
        const monthEnd = endOfMonth(currentDate);
        return {
          startDate: startOfWeek(monthStart),
          endDate: endOfWeek(monthEnd)
        };
      default:
        return { startDate: currentDate, endDate: currentDate };
    }
  }, [currentDate, viewMode]);

  // Load meetings data
  useEffect(() => {
    const loadCalendarData = async () => {
      if (!user || !currentTenant) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Calculate exact date range based on view mode and current date
        const { startDate, endDate } = getDateRangeForView();

        // Format dates as ISO strings (YYYY-MM-DD) for server-side filtering
        const startDateStr = format(startDate, 'yyyy-MM-dd');
        const endDateStr = format(endDate, 'yyyy-MM-dd');

        // Load meetings with custom date range for server-side filtering
        const meetingsData = await meetingService.getMeetings(
          currentTenant.id,
          undefined,  // patientId
          calendarTherapistId,  // therapistId (self-only when no all_patients)
          undefined,  // status
          undefined,  // dateRange
          startDateStr,  // startDate
          endDateStr,    // endDate
          true           // excludeCancelled
        );
        setMeetings(meetingsData);
      } catch (err) {
        console.error('Error loading calendar data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load calendar data');
      } finally {
        setLoading(false);
      }
    };

    loadCalendarData();
  }, [user, currentTenant, refreshKey, currentDate, viewMode, getDateRangeForView, calendarTherapistId]);

  const getMeetingColor = (status: string): string => {
    switch (status) {
      case 'active':
        return '#e0e7ff'; // Indigo
      case 'done':
        return '#dcfce7'; // Green
      case 'cancelled':
        return '#fee2e2'; // Red
      case 'noshow':
        return '#fef3c7'; // Amber
      default:
        return '#f3f4f6'; // Gray
    }
  };

  // Load the therapist list for the filter dropdown (all_patients users only).
  //
  // Administrators are left out, the same way the booking roster leaves them
  // out (offers(): user_type <> 'ADMIN'). An admin takes no appointments, so
  // filtering the calendar to one shows an empty column, and selecting one
  // opens the availability editor for somebody whose hours can never be
  // offered to a patient - time entered against a name no booking will ever
  // match.
  useEffect(() => {
    if (!hasAllPatients || !currentTenant) return;
    userService.getUsers(currentTenant.id)
      .then((all) => setTherapists(
        all.filter((u) => (u.user_type || '').toLowerCase() !== 'admin')))
      .catch(() => setTherapists([]));
  }, [hasAllPatients, currentTenant]);

  // Apply the client-side therapist / "Only Mine" filters to the loaded meetings.
  const filteredMeetings = meetings.filter((m) => {
    if (!hasAllPatients) return true; // server already scoped to own meetings
    if (onlyMine && user) return m.therapist_id === user.id;
    if (therapistFilter) return m.therapist_id === therapistFilter;
    return true;
  });

  const getMeetingsForDate = (date: Date) => {
    // Both sides as plain local dates. scheduled_at is clinic-local wall
    // clock, so parsing it as UTC and converting to a timezone moved a
    // meeting across midnight for anyone not sitting in that zone - the
    // same shift that mislabelled the column headers.
    const targetDateStr = format(date, 'yyyy-MM-dd');
    return filteredMeetings.filter(meeting =>
      format(parseWallClock(meeting.scheduled_at), 'yyyy-MM-dd') === targetDateStr
    );
  };

  const handlePrevious = () => {
    switch (viewMode) {
      case 'day':
        setCurrentDate(subDays(currentDate, 1));
        break;
      case '3days':
        setCurrentDate(subDays(currentDate, 3));
        break;
      case 'week':
        setCurrentDate(subWeeks(currentDate, 1));
        break;
      case 'month':
        setCurrentDate(subMonths(currentDate, 1));
        break;
    }
  };

  const handleNext = () => {
    switch (viewMode) {
      case 'day':
        setCurrentDate(addDays(currentDate, 1));
        break;
      case '3days':
        setCurrentDate(addDays(currentDate, 3));
        break;
      case 'week':
        setCurrentDate(addWeeks(currentDate, 1));
        break;
      case 'month':
        setCurrentDate(addMonths(currentDate, 1));
        break;
    }
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  const handleMeetingClick = (e: React.MouseEvent, meeting: Meeting) => {
    e.stopPropagation();
    setContextMenu({ x: e.clientX, y: e.clientY, meeting });
  };

  const handleMeetingContextMenu = (e: React.MouseEvent, meeting: Meeting) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({ x: e.clientX, y: e.clientY, meeting });
  };

const handleEditNotes = async (newNotes: string) => {
    if (!selectedMeetingForComments) return;
    try {
      setIsSavingComments(true);
      await meetingService.updateMeeting(selectedMeetingForComments.id, {
        notes: newNotes,
      });
      // Reload meetings
      const { startDate, endDate } = getDateRangeForView();
      const startDateStr = format(startDate, 'yyyy-MM-dd');
      const endDateStr = format(endDate, 'yyyy-MM-dd');
      const meetingsData = await meetingService.getMeetings(
        currentTenant!.id,
        undefined,
        calendarTherapistId,
        undefined,
        undefined,
        startDateStr,
        endDateStr,
        true
      );
      setMeetings(meetingsData);
      setIsEditCommentsModalOpen(false);
    } catch (err) {
      console.error('Failed to update notes:', err);
      throw err;
    } finally {
      setIsSavingComments(false);
    }
  };


  const getMeetingTooltip = (meeting: Meeting): string => {
    const startTime = parseDateAsUTC(meeting.scheduled_at);
    const endTime = new Date(startTime.getTime() + meeting.duration_minutes * 60000);
    const startTimeStr = formatTime(startTime, userTimezone);
    const endTimeStr = formatTime(endTime, userTimezone);

    const typeName = meeting.meeting_type?.name || meeting.title;
    const userType = meeting.meeting_type?.user_type;
    const firstLine = userType ? `${typeName} - ${userType}` : typeName;

    let tooltip = firstLine;
    tooltip += `\nPatient: ${patientLabel(meeting)}`;
    tooltip += `\nHM ID: ${patientHmId(meeting)}`;
    tooltip += `\nTime: ${startTimeStr} - ${endTimeStr}`;
    tooltip += `\nTherapist: ${meeting.therapist.full_name}`;
    tooltip += `\nStatus: ${meeting.status}`;
    if (meeting.notes?.trim()) {
      tooltip += `\nNotes: ${meeting.notes}`;
    }
    return tooltip;
  };

  const getMeetingDisplayText = (meeting: Meeting): string => {
    // Calculate end time
    const startTime = parseDateAsUTC(meeting.scheduled_at);
    const endTime = new Date(startTime.getTime() + meeting.duration_minutes * 60000);

    // Format times in user's timezone using the same utility function as the rest of the app
    const startTimeStr = formatTime(startTime, userTimezone);
    const endTimeStr = formatTime(endTime, userTimezone);

    // Format: "HH:MM - HH:MM patient_hm_id patient_name"
    // An Israeli meeting has no patient here, so its label IS the reference:
    // "IL 36024" rather than an id and a name.
    return `${startTimeStr} - ${endTimeStr} ${patientHmId(meeting)} ${patientLabel(meeting)}`.replace('  ', ' ');
  };

  const contextMenuOptions = contextMenu?.meeting ? [
    ...(contextMenu.meeting.team_url ? [{
      label: 'Start',
      icon: '▶️',
      onClick: async () => {
        const m = contextMenu.meeting;
        const usesRecallBot = m.meeting_type?.user_type === 'doctor' || m.meeting_type?.user_type === 'case_manager';
        if (usesRecallBot && m.id) {
          try {
            await meetingService.startMeeting(m.id);
          } catch (e) {
            console.error('Failed to start Recall bot:', e);
          }
        }
        window.open(m.team_url!, '_blank');
        setContextMenu(null);
      },
    }] : []),
    {
      label: 'View Meeting',
      icon: '👁️',
      onClick: () => {
        navigate(`/patients/${contextMenu.meeting.patient_id}?meeting_id=${contextMenu.meeting.id}`);
        setContextMenu(null);
      },
    },
    {
      // The patient's scheduling page, not a Yoman event page.
      //
      // A Yoman link could only move the single event it pointed at, and
      // moving one meeting in isolation is what left the rest of a
      // patient's chain inconsistent. This opens everything they are due,
      // against this clinic's own availability.
      label: 'Update Schedule',
      icon: '📅',
      onClick: () => {
        const uuid = contextMenu.meeting.patient?.uuid;
        // Name the meeting this was opened from. It only decides anything
        // when the patient has more than one active - then the page manages
        // THIS one rather than whichever comes first.
        if (!openPatientScheduling(uuid, { meetingId: contextMenu.meeting.id },
                                   contextMenu.meeting.patient?.id)) {
          toast.error('This patient has no scheduling link yet.');
        }
        setContextMenu(null);
      },
    },
    ...(contextMenu.meeting.status !== 'noshow' ? [{
      label: 'No Show',
      icon: '⛔',
      onClick: () => {
        setSelectedMeetingForNoShow(contextMenu.meeting);
        setIsNoShowModalOpen(true);
        setContextMenu(null);
      },
    }] : []),
    {
      label: 'Finish Treatment',
      icon: (
        <svg fill="#e23232" width="16px" height="16px" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path d="M4,1A1,1,0,0,0,3,2V22a1,1,0,0,0,2,0V17H20a1,1,0,0,0,1-1V4a1,1,0,0,0-1-1H5V2A1,1,0,0,0,4,1ZM7,15H5V13H7Zm0-4H5V9H7ZM17,5h2V7H17Zm0,4h2v2H17Zm0,4h2v2H17ZM13,5h2V7H13Zm0,4h2v2H13Zm0,4h2v2H13ZM9,5h2V7H9ZM9,9h2v2H9Zm0,4h2v2H9ZM7,5V7H5V5Z" />
        </svg>
      ),
      iconClassName: '',
      onClick: () => {
        setSelectedMeetingForFinish(contextMenu.meeting);
        setIsFinishTreatmentModalOpen(true);
        setContextMenu(null);
      },
    },
    ...(contextMenu.meeting.status !== 'cancelled' ? [{
      label: 'Cancel',
      icon: '🚫',
      onClick: () => {
        setSelectedMeetingForCancel(contextMenu.meeting);
        setIsCancelMeetingModalOpen(true);
        setContextMenu(null);
      },
    }] : []),
    {
      label: 'Send Email',
      icon: '📧',
      onClick: () => {
        setSelectedMeetingForContact(contextMenu.meeting);
        setEmailDialogOpen(true);
        setContextMenu(null);
      },
    },
    {
      label: 'Send SMS',
      icon: '📱',
      onClick: () => {
        setSelectedMeetingForContact(contextMenu.meeting);
        setSmsDialogOpen(true);
        setContextMenu(null);
      },
    },
    {
      label: 'Edit Comments',
      icon: '💬',
      onClick: () => {
        setSelectedMeetingForComments(contextMenu.meeting);
        setIsEditCommentsModalOpen(true);
        setContextMenu(null);
      },
    },
  ] : [];

  const PIXELS_PER_HOUR = 80;

  // Whose working hours the calendar shows - ONLY when a therapist is
  // picked in the filter.
  //
  // Not "yours when nothing is chosen": the unfiltered calendar is everyone's
  // meetings at once, and one person's working hours drawn across it would
  // read as the hours for all of them. Bands only mean something once the
  // column belongs to one therapist, so with no selection there is nothing
  // to show and nothing to drag.
  const availabilityUserId = therapistFilter === ''
    ? undefined : Number(therapistFilter);

  const loadAvailability = useCallback(async () => {
    if (!availabilityUserId) { setAvailability(null); return; }
    const { startDate, endDate } = getDateRangeForView();
    try {
      setAvailability(await availabilityService.get(
        format(startDate, 'yyyy-MM-dd'), format(endDate, 'yyyy-MM-dd'),
        availabilityUserId));
    } catch {
      // Not fatal: the calendar's job is the meetings, and a failed
      // availability read must not empty the page.
      setAvailability(null);
    }
  }, [availabilityUserId, getDateRangeForView]);

  useEffect(() => { loadAvailability(); }, [loadAvailability]);

  /** The hours that apply on a day, as the server resolved them. */
  /** The days the "offered" total should count.
   *
   *  The month view fetches startOfWeek(monthStart)..endOfWeek(monthEnd) -
   *  the whole 42-cell grid, which it has to draw - but the header above it
   *  names a single month, and a total that quietly includes the leading and
   *  trailing days of the neighbouring ones reads as the month being far
   *  bigger than it is. The other views show exactly the days they load, so
   *  only the month needs narrowing.
   */
  const offeredInPeriod = React.useMemo(() => {
    const days = availability?.days || [];
    if (viewMode !== 'month') return days;
    const prefix = format(currentDate, 'yyyy-MM-');
    return days.filter((d) => d.date.startsWith(prefix));
  }, [availability, viewMode, currentDate]);

  /** The day's resolved blocks, whatever kind, as the server sent them. */
  const resolvedOn = (day: Date) => {
    const key = format(day, 'yyyy-MM-dd');
    return availability?.days?.find((x) => x.date === key)?.blocks || [];
  };

  const asBlock = (b: { id?: number | null; start_time: string; end_time: string;
                        source?: string; comment?: string | null },
                   source: WorkingBlock['source']): WorkingBlock => ({
    id: b.id ?? null,
    start_time: b.start_time,
    end_time: b.end_time,
    source,
    comment: b.comment ?? null,
  });

  /** The day's WORKING hours. Neither refusal is among them.
   *
   *  The server returns cancelled and blocked time in the same list, marked,
   *  because the calendar has to draw them - but they are hours nobody may be
   *  given, so anything that treats this as "when can a patient come" must
   *  not see them. They are picked out by cancelledOn and blockedOn below.
   */
  const blocksFor = (day: Date): WorkingBlock[] =>
    resolvedOn(day)
      .filter((b) => b.source !== 'blocked' && b.source !== 'cancelled')
      .map((b) => asBlock(b, b.source === 'date' ? 'date' : 'weekly'));

  /** Occurrences of the pattern DELETED from this date - the hours are not
   *  there, and restoring one gives it back to the pattern. */
  const cancelledOn = (day: Date): WorkingBlock[] =>
    resolvedOn(day)
      .filter((b) => b.source === 'cancelled')
      .map((b) => asBlock(b, 'cancelled'));

  /** Hours the therapist has CLOSED on this date - still there, drawn, but
   *  never offered until the block is lifted. */
  const blockedOn = (day: Date): WorkingBlock[] =>
    resolvedOn(day)
      .filter((b) => b.source === 'blocked')
      .map((b) => asBlock(b, 'blocked'));

  /** Open the editor on an existing block. Shared with the month view, which
   *  has no time axis to drag on and so edits a day's blocks as a list. */
  const editBlock = (day: Date, b: WorkingBlock) => {
    setAvailabilityError(null);
    const weekly = (availability?.weekly || []).find((w) => w.id === b.id);
    setAvailabilityEditor({
      date: day, start: b.start_time, end: b.end_time,
      mode: 'edit', source: b.source,
      validFrom: weekly?.valid_from ?? null,
      validTo: weekly?.valid_to ?? null,
      comment: b.comment ?? null,
      availabilityId: b.id ?? null,
    });
  };

  /** Open the editor on a cancelled occurrence, to put it back. */
  const reviveBlock = (day: Date, b: WorkingBlock) => {
    setAvailabilityError(null);
    setAvailabilityEditor({
      date: day, start: b.start_time, end: b.end_time,
      mode: 'blocked', source: 'cancelled',
      comment: b.comment ?? null,
      availabilityId: b.id ?? null,
    });
  };

  /** Open the editor on a blocked span, to lift it. */
  const openBlocked = (day: Date, b: WorkingBlock) => {
    setAvailabilityError(null);
    setAvailabilityEditor({
      date: day, start: b.start_time, end: b.end_time,
      mode: 'closed', source: 'blocked',
      comment: b.comment ?? null,
      availabilityId: b.id ?? null,
    });
  };

  /** Add hours to a day. The month view cannot express a time by dragging,
   *  so it opens the editor on a working-day default and lets the pickers say
   *  the rest - which is the same editor the week view opens after a drag. */
  const addHoursTo = (day: Date) => {
    setAvailabilityError(null);
    setAvailabilityEditor({ date: day, start: '09:00', end: '17:00', mode: 'create' });
  };

  /** The day's blocks, shaped for the override API.
   *
   *  The WHOLE resolved day, weekly occurrences included. An override says
   *  how the day should END UP and the server stores the difference from the
   *  pattern, so an occurrence left OUT of the payload is an occurrence
   *  cancelled. These used to be filtered to `source === 'date'`, which was
   *  right while an override replaced its whole day and every block on an
   *  exceptional day was therefore dated; under the current reading it
   *  cancelled every untouched occurrence on the day instead.
   *
   *  Only a dated block sends its id, so saving the day updates that row
   *  rather than replacing it and its history stays continuous. A weekly
   *  block's id belongs to the PATTERN row, while the override's upsert
   *  matches ids among the day's own rows - passing it risks updating an
   *  unrelated one-off that shares the number. The server recognises a
   *  pattern block by its times, so withholding the id costs nothing. */
  const dayPayload = (day: Date): AvailabilityBlock[] =>
    blocksFor(day).map((b) => ({
      id: b.source === 'date' ? (b.id ?? undefined) : undefined,
      start_time: b.start_time,
      end_time: b.end_time,
      comment: b.comment,
    }));

  /** The day without the block the editor opened on.
   *
   *  Matched on those times rather than on an id: a day's blocks may not
   *  overlap, so the times identify one block exactly, while an id cannot -
   *  a weekly occurrence carries the pattern row's id, so an id match can
   *  also hit a one-off that happens to share the number. */
  const dayWithout = (day: Date, start: string, end: string) =>
    dayPayload(day).filter(
      (b) => !(b.start_time.slice(0, 5) === start.slice(0, 5)
               && b.end_time.slice(0, 5) === end.slice(0, 5)));

  const saveAvailability = async (payload: AvailabilitySave, day: Date) => {
    if (!availabilityUserId) return;
    setSavingAvailability(true);
    setAvailabilityError(null);
    try {
      const existingId = availabilityEditor?.availabilityId ?? undefined;
      if (payload.scope === 'weekly') {
        // The whole weekday is replaced, so it is sent as the day's full set
        // with this block edited or added - sending one block would delete
        // the others, which is how a morning shift ate an afternoon one.
        const weekday = day.getDay();
        const rest = (availability?.weekly || [])
          .filter((w) => w.weekday === weekday && w.id !== existingId);
        await availabilityService.setWeekly(weekday, [
          ...rest,
          {
            id: existingId, weekday,
            start_time: payload.start, end_time: payload.end,
            valid_from: payload.validFrom, valid_to: payload.validTo,
            comment: payload.comment,
          },
        ], availabilityUserId);
      } else {
        const key = format(day, 'yyyy-MM-dd');
        // The day as it should end up: everything already on it, minus the
        // block being edited, plus its new hours. On a create nothing matches
        // the editor's times, so nothing is dropped.
        const rest = dayWithout(day, availabilityEditor?.start ?? payload.start,
                                availabilityEditor?.end ?? payload.end);
        await availabilityService.setOverride(key, [
          ...rest,
          // Only a block that already WAS this date's own keeps its id. Giving
          // a weekly occurrence's id to a dated row would point it at the
          // pattern row instead.
          { id: availabilityEditor?.source === 'date' ? existingId : undefined,
            start_time: payload.start, end_time: payload.end,
            comment: payload.comment },
        ], availabilityUserId);
      }
      setAvailabilityEditor(null);
      await loadAvailability();
    } catch (e: any) {
      // Kept in the editor, which is still open and is where the fix is made.
      setAvailabilityError(
        e?.response?.data?.detail || 'Could not save these hours.');
    } finally {
      setSavingAvailability(false);
    }
  };

  type EditorState = NonNullable<typeof availabilityEditor>;

  /** Run one availability write, keeping any refusal in the editor - it is
   *  still open, and is where the correction gets made. */
  const writeAvailability = async (fn: () => Promise<unknown>, failed: string) => {
    if (!availabilityUserId) return;
    setSavingAvailability(true);
    setAvailabilityError(null);
    try {
      await fn();
      setAvailabilityEditor(null);
      await loadAvailability();
    } catch (e: any) {
      setAvailabilityError(e?.response?.data?.detail || failed);
    } finally {
      setSavingAvailability(false);
    }
  };

  /** Scope decides what "delete" means: the standing pattern, or this date's
   *  own hours. An empty set is how both are said. */
  const removeAvailability = (ed: EditorState) => writeAvailability(() => {
    const day = ed.date;
    if (ed.source === 'weekly') {
      const weekday = day.getDay();
      const rest = (availability?.weekly || []).filter(
        (w) => w.weekday === weekday && w.id !== ed.availabilityId);
      return availabilityService.setWeekly(weekday, rest, availabilityUserId);
    }
    // The day without it. Everything else on the day goes back unchanged -
    // dropping the weekly occurrences here cancelled them.
    return availabilityService.setOverride(
      format(day, 'yyyy-MM-dd'),
      dayWithout(day, ed.start, ed.end),
      availabilityUserId);
  }, 'Could not remove these hours.');

  /** Drop ONE occurrence of a weekly pattern, leaving the rest.
   *
   *  The day is sent back without it, and the server stores that as a
   *  cancellation of this occurrence alone - the pattern itself is untouched,
   *  the day's other blocks stay the pattern's own, and the occurrence shows
   *  as cancelled so it can be put back. */
  const cancelThisDate = (ed: EditorState) => writeAvailability(
    () => availabilityService.setOverride(
      format(ed.date, 'yyyy-MM-dd'),
      dayWithout(ed.date, ed.start, ed.end),
      availabilityUserId),
    'Could not cancel this date.');

  /** Put a cancelled occurrence back - as part of the weekly pattern.
   *
   *  The day is sent back WITH the occurrence restored. Because the server
   *  stores only the difference from the pattern, a block the pattern already
   *  gives has no dated row written for it: the slot is the pattern's again,
   *  it follows later changes to the week, and it recurs as it always did.
   *
   *  dayPayload is the whole resolved day, so every other occurrence goes
   *  back too - leaving one out is what cancels it, and sending only the
   *  date's own blocks plus the revived one cancelled all its neighbours. */
  const reviveAvailability = (ed: EditorState) => writeAvailability(
    () => availabilityService.setOverride(
      format(ed.date, 'yyyy-MM-dd'),
      [...dayPayload(ed.date),
       { start_time: ed.start, end_time: ed.end, comment: ed.comment }],
      availabilityUserId),
    'Could not restore these hours.');

  /** Close this slot. The hours stay in the diary; nobody may be given them.
   *
   *  The slot's OWN times, not the pickers': blocking takes a whole slot,
   *  and the pickers are there to say what the hours are. On a drag the two
   *  are the same thing - the dragged span is the slot being created, and
   *  blocking it puts up a blocked slot over time that had none. */
  const blockHours = (ed: EditorState, comment: string | null) =>
    writeAvailability(
      () => availabilityService.setBlock(
        format(ed.date, 'yyyy-MM-dd'), ed.start, ed.end, true, comment,
        availabilityUserId),
      'Could not block these hours.');

  /** Turn this block off. The slot becomes active again - it was a slot
   *  before and it is a slot after, which is the whole of what blocking
   *  means here. */
  const unblockHours = (ed: EditorState) => writeAvailability(
    () => availabilityService.setBlock(
      format(ed.date, 'yyyy-MM-dd'), ed.start, ed.end, false, null,
      availabilityUserId),
    'Could not unblock these hours.');

  const renderTimelineView = (days: Date[]) => {
    // Dynamically calculate hour range based on meetings in the user's timezone
    let minHour = 8;
    let maxHour = 18;

    if (filteredMeetings.length > 0) {
      const hours: number[] = [];
      filteredMeetings.forEach(m => {
        const { hour } = getHourMinuteInTimezone(parseDateAsUTC(m.scheduled_at), userTimezone);
        hours.push(hour);
      });
      minHour = Math.min(8, ...hours);
      maxHour = Math.max(18, ...hours);
    }

    const dayStartHour = minHour;
    const dayEndHour = maxHour;
    const hours = Array.from({ length: dayEndHour - dayStartHour + 1 }, (_, i) => dayStartHour + i);

    const positionedMeetingsMap: Record<string, PositionedMeeting[]> = {};
    for (const day of days) {
      const dayKey = format(day, 'yyyy-MM-dd');
      positionedMeetingsMap[dayKey] = getMeetingsForDayWithLayout(
        day,
        filteredMeetings,
        dayStartHour,
        PIXELS_PER_HOUR,
        userTimezone
      );
    }

    const minWidth = days.length === 1 ? 600 : days.length === 3 ? 600 : 800;

    return (
      <div style={{ backgroundColor: '#fff', borderRadius: 8, overflow: 'hidden', height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header — reserve the same scrollbar gutter as the scrollable body below so the
            day-column separators line up (overflowY hidden makes scrollbar-gutter apply). */}
        <div style={{ display: 'grid', gridTemplateColumns: `60px repeat(${days.length}, 1fr)`, minWidth, flexShrink: 0, overflowY: 'hidden', scrollbarGutter: 'stable' }}>
          {/* Time column header */}
          <div style={{ borderBottom: '1px solid #e5e7eb', padding: 12 }}></div>

          {/* Day headers */}
          {days.map((day, i) => {
            // Labelled from the day ITSELF, not converted to a timezone.
            //
            // `days` holds local midnights, and converting one to another
            // zone can land on the day before: viewing a London calendar
            // from Israel, 21 October 00:00 is still the 20th in London. The
            // header then said "Tue 20" over a column the rest of the page -
            // its meetings, its blocks, the editor a drag opens - treats as
            // Wednesday the 21st. Creating "every Tuesday" from that column
            // wrote Wednesday.
            //
            // The body is the authority because that is what the data is
            // keyed by, and what the user is pointing at.
            const dayStr = format(day, 'yyyy-MM-dd');
            const todayStr = format(new Date(), 'yyyy-MM-dd');
            const isToday = dayStr === todayStr;
            const dayNum = day.getDate();
            const weekday = format(day, 'EEE');
            return (
              <div key={i} style={{
                borderBottom: '1px solid #e5e7eb',
                borderLeft: '1px solid #e5e7eb',
                padding: 12,
                textAlign: 'center',
                fontWeight: isToday ? 600 : 500,
                color: isToday ? '#3b5d6b' : '#000',
                backgroundColor: '#fff'
              }}>
                <div style={{ fontSize: 12 }}>{weekday}</div>
                <div style={{ fontSize: 18 }}>{dayNum}</div>
              </div>
            );
          })}
        </div>

        {/* Scrollable time slots */}
        <div style={{ overflow: 'auto', flex: 1, scrollbarGutter: 'stable' }}>
          <div style={{ display: 'grid', gridTemplateColumns: `60px repeat(${days.length}, 1fr)`, minWidth }}>
            {/* Time column */}
            <div style={{ borderRight: '1px solid #e5e7eb', backgroundColor: '#f9fafb' }}>
              {hours.map(hour => (
                <div key={hour} style={{
                  borderTop: '1px solid #e5e7eb',
                  padding: '8px 4px',
                  fontSize: 10,
                  color: '#6b7280',
                  textAlign: 'right',
                  height: PIXELS_PER_HOUR
                }}>
                  {hour.toString().padStart(2, '0')}:00
                </div>
              ))}
            </div>

            {/* Day cells */}
            {days.map((day) => {
              const dayKey = format(day, 'yyyy-MM-dd');
              const positionedMeetings = positionedMeetingsMap[dayKey] || [];
              const containerHeight = hours.length * PIXELS_PER_HOUR;

              return (
                <div key={dayKey} style={{
                  borderLeft: '1px solid #e5e7eb',
                  position: 'relative',
                  minHeight: containerHeight,
                  backgroundColor: '#fff'
                }}>
                  {/* Hour grid lines */}
                  {hours.map(hour => (
                    <div key={hour} style={{
                      position: 'absolute',
                      top: (hour - dayStartHour) * PIXELS_PER_HOUR,
                      left: 0,
                      right: 0,
                      height: PIXELS_PER_HOUR,
                      borderTop: '1px solid #e5e7eb',
                      pointerEvents: 'none'
                    }} />
                  ))}

                  {/* Working hours, under the meetings (see AvailabilityLayer) */}
                  {availability && (
                    <AvailabilityLayer
                      blocks={blocksFor(day)}
                      // Both refusals are drawn together; the layer tells
                      // them apart by source, and so do the two callbacks.
                      blocked={[...cancelledOn(day), ...blockedOn(day)]}
                      firstHour={dayStartHour}
                      pixelsPerHour={PIXELS_PER_HOUR}
                      editable={!!availability.editable}
                      onCreate={(start, end) => {
                        setAvailabilityError(null);
                        setAvailabilityEditor({ date: day, start, end, mode: 'create' });
                      }}
                      onEditBlock={(_i, b) => editBlock(day, b)}
                      onReviveBlock={(b) => reviveBlock(day, b)}
                      onUnblock={(b) => openBlocked(day, b)}
                    />
                  )}

                  {/* Positioned meetings */}
                  {positionedMeetings.map(meeting => (
                    <div
                      key={meeting.id}
                      onClick={(e) => handleMeetingClick(e, meeting)}
                      onContextMenu={(e) => handleMeetingContextMenu(e, meeting)}
                      title={getMeetingTooltip(meeting)}
                      style={{
                        position: 'absolute',
                        top: `${meeting.top}px`,
                        left: `${meeting.left}%`,
                        width: `calc(${meeting.width}% - 4px)`,
                        height: `${meeting.height}px`,
                        backgroundColor: getMeetingColor(meeting.status),
                        padding: '4px 6px',
                        borderRadius: 3,
                        fontSize: 11,
                        overflow: 'hidden',
                        cursor: 'pointer',
                        border: '1px solid #000',
                        boxSizing: 'border-box',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-start'
                      }}
                    >
                      <div style={{ fontWeight: 600, fontSize: 10, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {getMeetingDisplayText(meeting)}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const render3DayView = () => {
    const threeDays = [currentDate, addDays(currentDate, 1), addDays(currentDate, 2)];
    return renderTimelineView(threeDays);
  };

  const renderDayView = () => {
    const timelineView = renderTimelineView([currentDate]);

    return (
      <div style={{ display: 'flex', justifyContent: 'center', width: '100%', height: '100%' }}>
        <div style={{ maxWidth: '900px', width: '100%' }}>
          {timelineView}
        </div>
      </div>
    );
  };

  const renderWeekView = () => {
    // Week starts on Monday.
    const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
    const weekEnd = endOfWeek(currentDate, { weekStartsOn: 1 });
    const days = eachDayOfInterval({ start: weekStart, end: weekEnd });
    return renderTimelineView(days);
  };

  const renderMonthView = () => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    const startDate = startOfWeek(monthStart);
    const endDate = endOfWeek(monthEnd);
    const days = eachDayOfInterval({ start: startDate, end: endDate });
    // Only when the bands would mean something: one therapist chosen, and
    // the server saying this user may change their hours.
    const canEditAvailabilityHere = !!availabilityUserId && !!availability?.editable;

    return (
      <div style={{ backgroundColor: '#fff', borderRadius: 8, height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Day headers */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          borderBottom: '1px solid #e5e7eb',
          flexShrink: 0
        }}>
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <div key={day} style={{
              padding: 12,
              textAlign: 'center',
              fontSize: 12,
              fontWeight: 600,
              color: '#6b7280'
            }}>
              {day}
            </div>
          ))}
        </div>

        {/* Calendar grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          gridAutoRows: 'minmax(120px, auto)',
          flex: 1,
          overflow: 'auto'
        }}>
          {days.map((day, index) => {
            // See the week header: the day itself, not converted.
            const dayStr = format(day, 'yyyy-MM-dd');
            const todayStr = format(new Date(), 'yyyy-MM-dd');
            const currentMonthStr = format(currentDate, 'yyyy-MM');
            const isCurrentMonth = dayStr.substring(0, 7) === currentMonthStr;
            const isToday = dayStr === todayStr;
            const dayNum = day.getDate();
            const dayMeetings = getMeetingsForDate(day);

            return (
              <div
                key={index}
                style={{
                  border: '1px solid #e5e7eb',
                  padding: 8,
                  backgroundColor: isCurrentMonth ? '#fff' : '#f9fafb',
                  opacity: isCurrentMonth ? 1 : 0.5
                }}
              >
                <div style={{
                  fontSize: 12,
                  fontWeight: isToday ? 600 : 400,
                  color: isToday ? '#3b5d6b' : '#000',
                  marginBottom: 4
                }}>
                  {dayNum}
                </div>

                {/* Availability, editable here too.
                    The month view has no time axis, so hours are listed
                    rather than dragged: a block opens the same editor the
                    week view opens, and "+" adds one. Without this, setting a
                    therapist's hours meant switching to the week view and
                    paging through the month a week at a time. */}
                {canEditAvailabilityHere && (
                  <div style={{ marginBottom: 4 }}>
                    {blocksFor(day).map((b, i) => (
                      <div
                        key={`a-${i}`}
                        onClick={(e) => { e.stopPropagation(); editBlock(day, b); }}
                        title={b.comment || 'Edit these hours'}
                        style={{
                          fontSize: 9, lineHeight: '14px', cursor: 'pointer',
                          padding: '0 4px', marginBottom: 1, borderRadius: 2,
                          backgroundColor: '#eef4f6', color: '#3b5d6b',
                          border: '1px solid #d7e3e8',
                          whiteSpace: 'nowrap', overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {b.start_time.slice(0, 5)}-{b.end_time.slice(0, 5)}
                      </div>
                    ))}
                    {cancelledOn(day).map((b, i) => (
                      <div
                        key={`c-${i}`}
                        onClick={(e) => { e.stopPropagation(); reviveBlock(day, b); }}
                        title="Cancelled - click to put these hours back"
                        style={{
                          fontSize: 9, lineHeight: '14px', cursor: 'pointer',
                          padding: '0 4px', marginBottom: 1, borderRadius: 2,
                          backgroundColor: '#fdeaea', color: '#9b2c2c',
                          border: '1px dashed #e9a8a8',
                          textDecoration: 'line-through',
                          whiteSpace: 'nowrap', overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {b.start_time.slice(0, 5)}-{b.end_time.slice(0, 5)}
                      </div>
                    ))}
                    {blockedOn(day).map((b, i) => (
                      <div
                        key={`k-${i}`}
                        onClick={(e) => { e.stopPropagation(); openBlocked(day, b); }}
                        title={`Blocked${b.comment ? ` - ${b.comment}` : ''} - click to unblock`}
                        style={{
                          fontSize: 9, lineHeight: '14px', cursor: 'pointer',
                          padding: '0 4px', marginBottom: 1, borderRadius: 2,
                          backgroundColor: '#fdeaea', color: '#9b2c2c',
                          border: '1px solid #e9a8a8',
                          whiteSpace: 'nowrap', overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {b.start_time.slice(0, 5)}-{b.end_time.slice(0, 5)}
                      </div>
                    ))}
                    <div
                      onClick={(e) => { e.stopPropagation(); addHoursTo(day); }}
                      title="Add working hours on this day"
                      style={{
                        fontSize: 9, lineHeight: '14px', cursor: 'pointer',
                        padding: '0 4px', borderRadius: 2, color: '#6b7f88',
                        border: '1px dashed #d7e3e8',
                      }}
                    >+ hours</div>
                  </div>
                )}

                <div style={{ fontSize: 10, overflow: 'hidden' }}>
                  {dayMeetings.slice(0, 3).map(meeting => (
                    <div
                      key={meeting.id}
                      onClick={(e) => handleMeetingClick(e, meeting)}
                      onContextMenu={(e) => handleMeetingContextMenu(e, meeting)}
                      title={getMeetingTooltip(meeting)}
                      style={{
                        backgroundColor: getMeetingColor(meeting.status),
                        padding: '2px 4px',
                        marginBottom: 2,
                        borderRadius: 2,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        cursor: 'pointer',
                        border: '2px solid transparent',
                        boxSizing: 'border-box'
                      }}
                    >
                      {getMeetingDisplayText(meeting)}
                    </div>
                  ))}
                  {dayMeetings.length > 3 && (
                    <div style={{ fontSize: 10, color: '#6b7280', marginTop: 2 }}>
                      +{dayMeetings.length - 3} more
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Month view shows just "July 2026"; other views (day/week/3days) also include the
  // day, e.g. "7 July 2026" (day-first to match DD/MM/YYYY convention). Week view shows
  // the Monday (week start) date.
  const headerDate = viewMode === 'week'
    ? startOfWeek(currentDate, { weekStartsOn: 1 })
    : currentDate;
  const monthName = new Intl.DateTimeFormat('en-GB', {
    ...(viewMode !== 'month' ? { day: 'numeric' } : {}),
    month: 'long',
    year: 'numeric',
    timeZone: userTimezone,
  }).format(headerDate);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <div>Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ color: 'red', padding: '16px', marginBottom: '16px' }}>
        Error loading calendar: {error}
      </div>
    );
  }

  return (
    <div style={{ height: 'calc(100vh - 60px - 48px)', display: 'flex', flexDirection: 'column' }}>
      {/* Filters/Controls */}
      <div style={{
        backgroundColor: '#fff',
        padding: '12px 16px',
        borderRadius: 8,
        marginBottom: 16,
        display: 'flex',
        gap: 16,
        alignItems: 'center',
        flexWrap: 'wrap',
        flexShrink: 0
      }}>
        {/* Therapist + Only Mine filters (all_patients users only) */}
        {hasAllPatients && (
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <select
              value={therapistFilter}
              onChange={(e) => setTherapistFilter(e.target.value ? Number(e.target.value) : '')}
              disabled={onlyMine}
              style={{ padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, backgroundColor: onlyMine ? '#f3f4f6' : '#fff' }}
            >
              <option value="">All therapists</option>
              {therapists.map((t) => (
                <option key={t.id} value={t.id}>{t.full_name}</option>
              ))}
            </select>
            <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13, cursor: 'pointer' }}>
              <input type="checkbox" checked={onlyMine} onChange={(e) => setOnlyMine(e.target.checked)} />
              Only Mine
            </label>
          </div>
        )}

        <div style={{ flex: 1 }} />

        {/* Date with prev/next arrows on either side */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button
            onClick={handlePrevious}
            aria-label="Previous"
            style={{ padding: '4px 10px', cursor: 'pointer', border: 'none', backgroundColor: 'transparent', fontSize: 20, lineHeight: 1, color: '#3b5d6b' }}
          >‹</button>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600, minWidth: 140, textAlign: 'center' }}>{monthName}</h2>
          <button
            onClick={handleNext}
            aria-label="Next"
            style={{ padding: '4px 10px', cursor: 'pointer', border: 'none', backgroundColor: 'transparent', fontSize: 20, lineHeight: 1, color: '#3b5d6b' }}
          >›</button>
        </div>

        {/* Hours this therapist is available in the period on screen.
            Only while a therapist is selected, which is the only time the
            availability bands are drawn and so the only time the number
            refers to anybody. */}
        {availabilityUserId && availability && (
          <span
            title="Hours this therapist is offering in the period shown - the availability itself, not reduced by meetings already booked into it"
            style={{
              padding: '4px 10px', borderRadius: 4, fontSize: 12,
              fontWeight: 600, color: '#3b5d6b',
              backgroundColor: '#eef4f6', border: '1px solid #d7e3e8',
              whiteSpace: 'nowrap',
            }}
          >
            {availableHHMM(offeredInPeriod)} offered
          </span>
        )}

        {/* View buttons */}
        <div style={{ display: 'flex', gap: 4 }}>
          {[
            { key: 'month', label: 'Month' },
            { key: 'week', label: 'Week' },
            { key: '3days', label: '3 Days' },
            { key: 'day', label: 'Day' }
          ].map((v) => (
            <button
              key={v.key}
              onClick={() => setViewMode(v.key as ViewMode)}
              style={{
                padding: '6px 12px',
                border: viewMode === v.key ? '1px solid #3b5d6b' : '1px solid #d1d5db',
                backgroundColor: viewMode === v.key ? '#3b5d6b' : '#fff',
                color: viewMode === v.key ? '#fff' : '#000',
                borderRadius: 4,
                fontSize: 12,
                fontWeight: 500,
                cursor: 'pointer'
              }}
            >
              {v.label}
            </button>
          ))}
        </div>

        <button
          onClick={handleToday}
          style={{
            padding: '6px 16px',
            border: '1px solid #d1d5db',
            backgroundColor: '#fff',
            borderRadius: 6,
            fontSize: 14,
            fontWeight: 500,
            cursor: 'pointer'
          }}
        >
          Today
        </button>
      </div>

      {/* Calendar Container */}
      <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {viewMode === 'day' && renderDayView()}
        {viewMode === '3days' && render3DayView()}
        {viewMode === 'week' && renderWeekView()}
        {viewMode === 'month' && renderMonthView()}
      </div>

      {/* Context Menu */}
      {contextMenu && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          options={contextMenuOptions}
          onClose={() => setContextMenu(null)}
        />
      )}

      {/* Meeting Detail Modal */}

      {/* Edit Comments Modal */}
      {selectedMeetingForComments && (
        <EditCommentsModal
          isOpen={isEditCommentsModalOpen}
          onClose={() => {
            setIsEditCommentsModalOpen(false);
            setSelectedMeetingForComments(null);
          }}
          initialNotes={selectedMeetingForComments.notes || ''}
          onSave={handleEditNotes}
          isSaving={isSavingComments}
        />
      )}

      {/* Working hours editor */}
      {availabilityEditor && (
        <AvailabilityEditor
          open
          onClose={() => { setAvailabilityEditor(null); setAvailabilityError(null); }}
          date={availabilityEditor.date}
          start={availabilityEditor.start}
          end={availabilityEditor.end}
          mode={availabilityEditor.mode}
          source={availabilityEditor.source}
          validFrom={availabilityEditor.validFrom}
          validTo={availabilityEditor.validTo}
          comment={availabilityEditor.comment}
          availabilityId={availabilityEditor.availabilityId}
          therapistUserId={availabilityUserId}
          saving={savingAvailability}
          error={availabilityError}
          onSave={(payload) => saveAvailability(payload, availabilityEditor.date)}
          onDelete={() => removeAvailability(availabilityEditor)}
          onBlockThisDate={() => cancelThisDate(availabilityEditor)}
          onRevive={() => reviveAvailability(availabilityEditor)}
          onBlockHours={(comment) => blockHours(availabilityEditor, comment)}
          onUnblock={() => unblockHours(availabilityEditor)}
        />
      )}

      {/* Cancel Meeting Modal */}
      {selectedMeetingForCancel && (
        <CancelMeetingModal
          isOpen={isCancelMeetingModalOpen}
          onClose={() => {
            setIsCancelMeetingModalOpen(false);
            setSelectedMeetingForCancel(null);
          }}
          meetingId={selectedMeetingForCancel.id}
          onSuccess={() => {
            // Reload meetings
            const { startDate, endDate } = getDateRangeForView();
            const startDateStr = format(startDate, 'yyyy-MM-dd');
            const endDateStr = format(endDate, 'yyyy-MM-dd');
            meetingService.getMeetings(
              currentTenant!.id,
              undefined,
              calendarTherapistId,
              undefined,
              undefined,
              startDateStr,
              endDateStr,
              true
            ).then(setMeetings);
          }}
        />
      )}

      {/* No Show Modal */}
      {selectedMeetingForNoShow && (
        <NoShowModal
          isOpen={isNoShowModalOpen}
          onClose={() => {
            setIsNoShowModalOpen(false);
            setSelectedMeetingForNoShow(null);
          }}
          meetingId={selectedMeetingForNoShow.id}
          onSuccess={() => {
            // Reload meetings
            const { startDate, endDate } = getDateRangeForView();
            const startDateStr = format(startDate, 'yyyy-MM-dd');
            const endDateStr = format(endDate, 'yyyy-MM-dd');
            meetingService.getMeetings(
              currentTenant!.id,
              undefined,
              calendarTherapistId,
              undefined,
              undefined,
              startDateStr,
              endDateStr,
              true
            ).then(setMeetings);
          }}
        />
      )}

      {/* Finish Treatment Modal */}
      {selectedMeetingForFinish && (
        <FinishTreatmentModal
          isOpen={isFinishTreatmentModalOpen}
          onClose={() => {
            setIsFinishTreatmentModalOpen(false);
            setSelectedMeetingForFinish(null);
          }}
          patientId={selectedMeetingForFinish.patient_id as number}
          patientName={patientLabel(selectedMeetingForFinish)}
          onSuccess={() => {
            const { startDate, endDate } = getDateRangeForView();
            const startDateStr = format(startDate, 'yyyy-MM-dd');
            const endDateStr = format(endDate, 'yyyy-MM-dd');
            meetingService.getMeetings(
              currentTenant!.id,
              undefined,
              calendarTherapistId,
              undefined,
              undefined,
              startDateStr,
              endDateStr,
              true
            ).then(setMeetings);
          }}
        />
      )}

      {/* Email Send Dialog */}
      {selectedMeetingForContact && (
        <EmailSendDialog
          open={emailDialogOpen}
          onClose={() => {
            setEmailDialogOpen(false);
            setSelectedMeetingForContact(null);
          }}
          meetingId={selectedMeetingForContact.id}
          patientName={patientLabel(selectedMeetingForContact)}
          patientEmail={selectedMeetingForContact.patient?.email ?? null}
          context="MEETING"
          onSendSuccess={(message) => toast.success(message)}
          onSendError={(error) => toast.error(error)}
        />
      )}

      {/* SMS Send Dialog */}
      {selectedMeetingForContact && (
        <SmsSendDialog
          open={smsDialogOpen}
          onClose={() => {
            setSmsDialogOpen(false);
            setSelectedMeetingForContact(null);
          }}
          meetingId={selectedMeetingForContact.id}
          patientName={patientLabel(selectedMeetingForContact)}
          patientPhone={selectedMeetingForContact.patient?.phone ?? null}
          context="MEETING"
          onSendSuccess={(message) => toast.success(message)}
          onSendError={(error) => toast.error(error)}
        />
      )}
    </div>
  );
};

export default CalendarPage;
